import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, Snackbar } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import {
  AppState,
  useDispatch,
  WatchActions,
  WatchFormActions,
  WatchFormSelectors,
} from "../../state";
import { Formik, Form } from "formik";
import { AddWatchStepper, WatchHeader } from "./Components";
import { ScopeActions } from "../../state/scopes/actions";
import { useStyles } from "./AddWatchPage.styles";
import { authGet, authPatch, authPut, Navigation, AuthRequest } from "../../lib";
import { AuthSelectors } from "../../state/auth/selectors";
import { WatchFormState } from "../../state/watchform/state";
import { PhotosByFeed, Feed } from "../../types";
import { buildImageUrl, cleanModelNo } from "./Components/Images";

const EMPTY_PHOTOS: PhotosByFeed = {
  prestige_time: [],
  brand: [],
  watch_source: [],
  chrono24: [],
};

const useLoadingStyles = makeStyles((theme) => ({
  dot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    animation: "$bounce 1.4s ease-in-out infinite both",
    "&:nth-child(1)": { animationDelay: "-0.32s" },
    "&:nth-child(2)": { animationDelay: "-0.16s" },
    "&:nth-child(3)": { animationDelay: "0s" },
  },
  "@keyframes bounce": {
    "0%, 80%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
    "40%": { transform: "scale(1.0)", opacity: 1 },
  },
}));

export function AddWatchPage(props) {
  const siteId = useSelector((state: AppState) => state.scopes.site_id);
  const watchId = props.pageRoute.match.params.id;
  const isEdit = props.pageRoute.page.type === "PAGE_EDIT_WATCH";
  const [watchData, setWatchData] = useState(false);
  const dispatch = useDispatch();
  const listsLoaded = useSelector((state: AppState) => state.watches.watchLists.brand.length > 0);

  useEffect(() => {
    if (!listsLoaded) {
      dispatch(
        WatchActions.getList([
          "brand",
          "series",
          "series_group",
          "size",
          "warranty",
          "status",
          "vendor",
          "ship_tier",
          "delivery",
          "material",
          "edition",
        ]),
      );
    }
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isEdit && siteId !== 0) {
    return null;
  }
  return <RenderWatchPage watchId={watchId} isEdit={isEdit} />;
}

export function RenderWatchPage(props) {
  const { watchId, isEdit } = props;
  const apiReqInProgress = useRef(false);
  const dispatch = useDispatch();
  const classes = useStyles();
  const lc = useLoadingStyles();

  useEffect(() => {
    document.getElementById('__watch-nav-overlay')?.remove();
    window.scrollTo(0, 1);
    window.scrollTo(0, 0);
    if (isEdit && localStorage.getItem('watchJustAdded') === '1') {
      localStorage.removeItem('watchJustAdded');
      setShowAddedSuccess(true);
    }
    if (isEdit && localStorage.getItem('watchJustUpdated') === '1') {
      localStorage.removeItem('watchJustUpdated');
      setShowUpdatedSuccess(true);
    }
  }, [isEdit]);

  const [retired, setRetired] = useState(false);
  const [showAddedSuccess, setShowAddedSuccess] = useState(false);
  const [showUpdatedSuccess, setShowUpdatedSuccess] = useState(false);
  const [editWatch, setEditWatch] = useState<undefined | WatchFormState>();
  const [statusID, setStatusID] = useState();
  const duplicateRef = useRef(false);
  const setDuplicate = (val: boolean) => { duplicateRef.current = val; };
  const [tpc_retail, setTpc_retail] = useState(true);
  const [photosByFeed, setPhotosByFeed] = useState<PhotosByFeed>({ ...EMPTY_PHOTOS });
  const siteId = useSelector((state: AppState) => state.scopes.site_id);
  const watchform = useSelector(WatchFormSelectors.watchform);
  const currentUser = useSelector(AuthSelectors.userFullName);
  const setFormatEditWatch = (state) => {
    const _editWatch = {
      ...state,
      water_resistance_m: parseInt(state.water_resistance_m),
      water_resistance_ft: parseInt(state.water_resistance_ft),
      show_recent: Boolean(state.show_recent),
      show_reduced: Boolean(state.show_reduced),
      price_on_special: Boolean(state.price_on_special),
      retired: Boolean(state.retired),
      papers_included: Boolean(state.papers_included),
      show_display_date: Boolean(state.show_display_date),
      display_date_hide_month: Boolean(state.display_date_hide_month),
      hide_retail: Boolean(state.hide_retail),
    };
    setEditWatch(_editWatch);
  };

  useEffect(() => {
    apiReqInProgress.current = false;
    (async function () {
      if (isEdit && apiReqInProgress.current === false) {
        apiReqInProgress.current = true;
        const response = await authGet(`item/${watchId}?site=${siteId}`);
        const { error, data } = response;
        if (error) return;
        setFormatEditWatch(data);
        const byFeed: PhotosByFeed = { ...EMPTY_PHOTOS };
        if (data?.item_images?.length) {
          for (const img of data.item_images) {
            const feed = img.feed as Feed;
            byFeed[feed].push({
              image_id:    img.image_id,
              previewUrl:  buildImageUrl(img.feed, watchId, img.filename, data.brand_id, data.series_id, data.model_no) ?? "",
              caption:     img.caption,
              own_pic:     img.own_pic,
              is_shareable: img.is_shareable, // dormant column kept on row for round-trip
              pt_display:  img.pt_display,
              sort_order:  img.sort_order,
              file_size:   img.file_size,
              width:       img.width,
              height:      img.height,
              uploaded_by: img.uploaded_by,
              created_at:  img.created_at,
            });
          }
        }
        setPhotosByFeed(byFeed);
      } else if (!isEdit) {
        setFormatEditWatch(watchform);
      }
    })();
  }, [isEdit, siteId, watchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadImages = async (modelNo: string, itemId: number) => {
    const feeds: Feed[] = ["prestige_time", "brand", "watch_source", "chrono24"];
    for (const feed of feeds) {
      const photos = photosByFeed[feed];
      const newPhotos = photos.filter((p) => !!p.file);
      const existingPhotos = photos.filter((p) => !!p.image_id && !p.file);

      for (const photo of newPhotos) {
        const fd = new FormData();
        fd.append("File", photo.file as File);
        fd.append("0", photo.caption ?? "");
        fd.append("is_shareable", String(photo.is_shareable ?? 1));
        fd.append("pt_display",   String(photo.pt_display ?? 1));
        if (feed === "brand") {
          fd.append("own_pic", String(photo.own_pic ?? 0));
        }
        fd.append("uploaded_by", currentUser);
        await AuthRequest.post(
          // cleanModelNo: `/`→`~`, ` `→`_`, `.`→`-` so URL never needs %-encoding.
          // Apache (AllowEncodedSlashes Off by default) rejects %2F → 404. Backend's
          // buildFilename runs cleanModelNo too — idempotent on the cleaned value.
          `/images/${cleanModelNo(modelNo)}?item=${itemId}&feed=${feed}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      }

      for (const photo of existingPhotos) {
        await authPatch(`/images/${photo.image_id}`, {
          is_shareable: photo.is_shareable,
          pt_display:   photo.pt_display,
          caption:      photo.caption,
          sort_order:   photo.sort_order,
        });
      }
    }
  };

  if (!editWatch) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" style={{ height: "60vh" }}>
        <Box display="flex" style={{ gap: 10 }}>
          <div className={lc.dot} />
          <div className={lc.dot} />
          <div className={lc.dot} />
        </Box>
      </Box>
    );
  }

  return (
    <Box style={{ width: "100%" }}>
      <Snackbar
        open={showUpdatedSuccess}
        autoHideDuration={4000}
        onClose={() => setShowUpdatedSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ top: 80 }}
      >
        <Alert severity="success" onClose={() => setShowUpdatedSuccess(false)}>
          Watch updated!
        </Alert>
      </Snackbar>
      <Snackbar
        open={showAddedSuccess}
        autoHideDuration={4000}
        onClose={() => setShowAddedSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        style={{ top: 80 }}
      >
        <Alert severity="success" onClose={() => setShowAddedSuccess(false)}>
          Watch successfully added!
        </Alert>
      </Snackbar>
      <Formik
        initialValues={{ ...editWatch }}
        onSubmit={async (values, { setSubmitting }) => {
          if (duplicateRef.current) {
            duplicateRef.current = false;
            const data: any = await dispatch(
              WatchActions.addWatch(`/item?site=${siteId}`, values, true),
            );
            if (data?.item_id) {
              if (tpc_retail) {
                await authPut(`/site_share/${data.item_id}?site=2`, {
                  price_retail: parseInt(data.price_retail),
                });
              }
              Navigation.go(`/edit-watch/${data.item_id}`);
            }
          } else if (isEdit) {
            const data: any = await dispatch(
              WatchActions.editWatch(`/item/${watchId}?site=${siteId}`, values),
            );
            if (data?.item_id) {
              await uploadImages(values.model_no, data.item_id);
              if (tpc_retail) {
                await authPut(`/site_share/${data.item_id}?site=2`, {
                  price_retail: parseInt(data.price_retail),
                });
              }
              localStorage.setItem('watchJustUpdated', '1');
              window.location.reload();
            }
          } else {
            const data: any = await dispatch(
              WatchActions.addWatch(`/item?site=${siteId}`, values),
            );
            if (data?.item_id) {
              await uploadImages(values.model_no, data.item_id);
              if (tpc_retail) {
                await authPut(`/site_share/${data.item_id}?site=2`, {
                  price_retail: parseInt(data.price_retail),
                });
              }
              localStorage.setItem('watchJustAdded', '1');
              Navigation.go(`/edit-watch/${data.item_id}`);
            }
          }

          setTimeout(() => setSubmitting(true), 500);
        }}
      >
        {({ submitForm, isSubmitting, setFieldValue, values }) => (
          <Form className={classes.centerMaxWidth}>
            <AddWatchStepper
              tpc_retail={tpc_retail}
              setTpc_retail={setTpc_retail}
              classes={classes}
              watchId={watchId}
              isEdit={isEdit}
              setFieldValue={setFieldValue}
              submitForm={submitForm}
              setStatus={setStatusID}
              setDuplicate={setDuplicate}
              retired={retired}
              setRetired={setRetired}
              setPhotosByFeed={setPhotosByFeed}
              photosByFeed={photosByFeed}
              brandId={values.brand_id}
              seriesId={values.series_id}
            />
          </Form>
        )}
      </Formik>
    </Box>
  );
}
